import {
  Alert,
  Button,
  Col,
  DatePicker,
  Divider,
  Form,
  Input,
  message,
  Modal,
  Row,
  Space,
  Tag,
} from "antd";
import dayjs from "dayjs";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ParseMessage } from "~/components/atom/parseMessage.jsx";
import { HelpLine } from "~/components/help/helpLine.jsx";
import { $t, i18n } from "~/i18n.js";
import { api } from "~/lib/api.js";
import { BreadcrumbHeader } from "../../components/layout/breadcrumbHeader.jsx";
import { MainLayout } from "../../components/layout/layout.jsx";
import { LedgerTable } from "../admin/financial/ledgers/LedgerTable.jsx";
import { DirectDebit } from "./directDebit.jsx";
import { HelpBubble } from "~/components/help/helpBubble.jsx";

export const MyAccountPage = () => {
  const dispatch = useDispatch();
  const [isPending, setIsPending] = useState(true);
  const [ledgerPending, setLedgerPending] = useState(false);
  const [ledger, setLedger] = useState([]);
  const [mandateInfo, setMandateInfo] = useState({
    status: null,
    date_created: null,
    last_three_digits_iban: null,
  });
  const [hasOpenPayments, setHasOpenPayments] = useState(false);
  const [ledgerBalance, setLedgerBalance] = useState(0);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [invoiceModalOpen, setInvoiceModalOpen] = useState(false);
  const [ledgerPage, setLedgerPage] = useState(1);
  const [ledgerPagination, setLedgerPagination] = useState({ count: null });
  const [payAmount, setPayAmount] = useState(1);
  const [loading, setLoading] = useState(false);
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [invoiceForm] = Form.useForm();

  const { club } = useSelector((state) => state.persist);

  const paymentTestMode = club.payment_testmode;
  const paymentDetails = club?.payment_details || "";
  
  // Default dates
  const today = dayjs();
  const janFirst = dayjs().startOf("year");

  const openPaymentModal = () => {
    if (ledgerBalance <= 0) {
      setPayAmount(
        Math.max(-1 * ledgerBalance, club.minimum_payment_amount || 1),
      );
    } else {
      setPayAmount(club.minimum_payment_amount || 1);
    }
    setPaymentModalOpen(true);
  };

  // Fetch ledger entries with pagination and optional open payment check
  const fetchLedger = async (page = 1, checkOpenPayments = false) => {
    if (paymentTestMode) return setIsPending(false);
    setLedgerPending(true);
    let url = `financial/accounts/my_account.json?page=${page}`;
    if (checkOpenPayments) url += "&check_open_payments=1";
    const data = await api.get(url);
    setLedger(data?.results || []);
    const balance = data?.ledger_balance || 0;
    setLedgerBalance(balance);
    dispatch({
      type: "SET_BALANCE",
      balance: balance,
    });
    setMandateInfo({
      status: data?.mandate_status,
      date_created: data?.mandate_date_created,
      last_three_digits_iban: data?.last_three_digits_iban,
    });
    setLedgerPagination({ count: data?.count });
    setIsPending(false);
    setLedgerPending(false);
    setHasOpenPayments(
      Array.isArray(data?.results) &&
        data.results.some((l) => l.payment_status === "open"),
    );
  };

  useEffect(() => {
    fetchLedger(1);
    setLedgerPage(1);
  }, []);

  // Placeholder for online payment handler
  const payOnline = async (amount) => {
    // Implement payment logic here
    setLoading(true);
    const data = await api.post("financial/accounts/create_payment.json", {
      amount: payAmount,
    });
    if (data && data.payment_link) {
      // Redirect to the payment link
      window.location.href = data.payment_link;
    } else {
      setLoading(false);
      setPaymentModalOpen(false);
    }
  };

  const handleDownloadInvoice = async () => {
    try {
      setInvoiceLoading(true);
      const values = await invoiceForm.validateFields();
      const date_from = values.date_from.format("YYYY-MM-DD");
      const date_to = values.date_to.format("YYYY-MM-DD");
      const url = `financial/accounts/create_invoice.json?date_from=${date_from}&date_to=${date_to}`;
      const fileName = `${$t("factuur")}_${date_from}_${date_to}.pdf`;

      await api.open_file(url, fileName);

      setInvoiceModalOpen(false);
    } catch (e) {
      message.error($t("Selecteer een geldige periode."));
    } finally {
      setInvoiceLoading(false);
    }
  };

  return (
    <MainLayout isLoading={isPending}>
      <BreadcrumbHeader
        breadcrumbs={[
          <p key="1" className="title">
            {$t("Mijn betalingen")}
          </p>,
        ]}
        buttons={[]}
      />

      {paymentTestMode && (
        <Alert
          message={$t(
            "Betalingen zijn nog niet geactiveerd. Zodra de betalingen geactiveerd zijn, kun je hier betalen.",
          )}
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      <Modal
        open={paymentModalOpen}
        onCancel={() => setPaymentModalOpen(false)}
        title={$t("Betaal")}
        footer={null}
      >
        <div>
          <p className="label spacer">{$t("Betaal online")}</p>
          <Space.Compact style={{ width: "100%" }}>
            <Input
              addonBefore={i18n.currencySymbol}
              type="number"
              min={club.minimum_payment_amount || 1}
              step={1}
              value={payAmount}
              onChange={(e) => setPayAmount(Number(e.target.value))}
              style={{ marginLeft: 8, width: 120 }}
            />
            <Button
              type="primary"
              disabled={
                paymentTestMode ||
                payAmount < (club.minimum_payment_amount || 1)
              }
              onClick={payOnline}
              loading={loading}
            >
              {" "}
              {$t("Betaal Online")}
            </Button>
          </Space.Compact>
          {payAmount < (club.minimum_payment_amount || 1) && (
            <Alert
              message={$t(
                `Minimum bedrag is ${i18n.parseLocaleCurrencyString(club.minimum_payment_amount || 1)}`,
              )}
              type="error"
              style={{ marginTop: 8 }}
            />
          )}
        </div>
        <Divider />
        <ParseMessage message={paymentDetails} />
      </Modal>

      <Modal
        open={invoiceModalOpen}
        onCancel={() => setInvoiceModalOpen(false)}
        title={$t("Factuur downloaden")}
        footer={null}
      >
        <Form
          form={invoiceForm}
          layout="horizontal"
          initialValues={{ date_from: janFirst, date_to: today }}
          requiredMark={false}
        >
          <Form.Item
            name="date_from"
            label={$t("Van")}
            rules={[{ required: true }]}
          >
            {" "}
            <DatePicker format="DD-MM-YYYY" style={{ width: "100%" }} />{" "}
          </Form.Item>
          <Form.Item
            name="date_to"
            label={$t("Tot en met")}
            rules={[{ required: true }]}
          >
            {" "}
            <DatePicker format="DD-MM-YYYY" style={{ width: "100%" }} />{" "}
          </Form.Item>
          <Button
            type="primary"
            loading={invoiceLoading}
            onClick={handleDownloadInvoice}
            style={{ width: "100%" }}
          >
            {$t("Download PDF")}
          </Button>
        </Form>
      </Modal>

      <Row align="middle" justify="space-between" style={{ marginTop: 16 }}>
        <Col style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span className="label">{$t("Saldo")}</span>{" "}
          <Tag
            color={ledgerBalance < 0 ? "red" : "green"}
            style={{ fontWeight: 500, fontSize: 16, padding: 4 }}
          >
            {ledgerBalance < 0
              ? `-${i18n.parseLocaleCurrencyString(Math.abs(ledgerBalance))}`
              : `+${i18n.parseLocaleCurrencyString(ledgerBalance)}`}
          </Tag>

          {club.has_mollie && (
            <Button
              disabled={paymentTestMode}
              type={ledgerBalance < 0 ? "primary" : "default"}
              onClick={openPaymentModal}
            >
              {$t("Betaal")}
            </Button>
          )}

          {!club.has_mollie &&  paymentDetails && <HelpBubble size={16} content={paymentDetails} />}
        </Col>
        <Col>
          <Button
            type="default"
            disabled={paymentTestMode}
            onClick={() => setInvoiceModalOpen(true)}
          >
            {$t("Download factuur")}
          </Button>
        </Col>
      </Row>

      {!paymentTestMode && hasOpenPayments && (
        <div style={{ marginTop: 24 }}>
          <Button
            onClick={async () => {
              await fetchLedger(ledgerPage, true);
            }}
            type="primary"
            loading={isPending}
          >
            {$t("Check betalingen")}
          </Button>
          <HelpLine style={{ marginTop: 8 }}>
            {$t(
              "Er staan nog online betalingen open die nog verwerkt moeten worden.",
            )}{" "}
            <br />
            {$t(
              "Het kan enkele uren duren voordat betalingen goed verwerkt zijn.",
            )}
          </HelpLine>
        </div>
      )}

      <DirectDebit
        club={club}
        paymentTestMode={paymentTestMode}
        mandateInfo={mandateInfo}
        setMandateInfo={setMandateInfo}
      />

      <p className="title spacer" style={{ marginTop: 24 }}>
        {$t("Mutaties")}
      </p>

      <LedgerTable
        ledgerEntries={ledger}
        loading={ledgerPending}
        setOpenLedger={null}
        hasUsers={false}
        pagination={ledgerPagination}
        currentPageNumber={ledgerPage}
        setCurrentPage={setLedgerPage}
        getLedgers={fetchLedger}
      />
    </MainLayout>
  );
};
